import { ModalBase, OverlineTitle, STATUSPAGE } from '@repo/ui';
import {
  GetCourseSettingsFromEntityUseCaseInterface,
  UpdateCourseFromEntityUseCaseInterface,
} from 'domain/usecases';
import { useFormik } from 'formik';
import { notifyError, notifySuccess } from 'presentation/app/components/notify';
import { useService } from 'presentation/app/hooks/useService';
import { useCallback, useState } from 'react';
import { Col, Form, Input, Label, Row } from 'reactstrap';

interface IProps {
  isOpen: boolean;
  onUpdated?: () => void;
  toggleModal: () => void;
  relationshipId: number | null;
  courseSettings: GetCourseSettingsFromEntityUseCaseInterface.Result | null;
}

export const ModalCourseSettings = ({
  isOpen,
  relationshipId,
  courseSettings,
  toggleModal,
  onUpdated,
}: IProps) => {
  // Hooks
  const { entitiesService } = useService();

  // States
  const [
    requestUpdateCourseFromEntityStatus,
    setRequestUpdateCourseFromEntityStatus,
  ] = useState(STATUSPAGE.DONE);

  // Methods
  const requestUpdateCourseFromEntity = useCallback(
    async (params: UpdateCourseFromEntityUseCaseInterface.Params) => {
      setRequestUpdateCourseFromEntityStatus(STATUSPAGE.LOADING);
      await entitiesService
        .updateCourseFromEntity(params)
        .then((res) => {
          notifySuccess(res.message);
          onUpdated?.();
          toggleModal();
        })
        .catch((err) => {
          notifyError(err.message);
        })
        .finally(() => {
          setRequestUpdateCourseFromEntityStatus(STATUSPAGE.DONE);
        });
    },
    [onUpdated, toggleModal],
  );

  // Form Validation
  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      locationToleratedDistanceInMeters:
        courseSettings?.academicSettings?.locationToleratedDistanceInMeters ??
        '',
      isProfessionalMandatoryInActivity:
        courseSettings?.studentSettings?.isProfessionalMandatoryInActivity ??
        false,

      allowEvaluationAcceptanceByStudent:
        courseSettings?.studentSettings?.allowEvaluationAcceptanceByStudent ??
        false,

      allowCidCiapUponActivityCompletion:
        courseSettings?.studentSettings?.allowCidCiapUponActivityCompletion ??
        false,

      requestToSelectProfessionalAfterActivityCompletion:
        courseSettings?.studentSettings
          ?.requestToSelectProfessionalAfterActivityCompletion ?? false,

      compelToSelectProfessionalAfterActivityCompletion:
        courseSettings?.studentSettings
          ?.compelToSelectProfessionalAfterActivityCompletion ?? false,
    },
    onSubmit: (values) => {
      requestUpdateCourseFromEntity({
        id: relationshipId,
        academicSettings: {
          locationToleratedDistanceInMeters:
            values.locationToleratedDistanceInMeters === '' ||
            values.locationToleratedDistanceInMeters === null
              ? null
              : Number(values.locationToleratedDistanceInMeters),
        },
        studentSettings: {
          isProfessionalMandatoryInActivity:
            values.isProfessionalMandatoryInActivity,
          allowEvaluationAcceptanceByStudent:
            values.allowEvaluationAcceptanceByStudent,
          allowCidCiapUponActivityCompletion:
            values.allowCidCiapUponActivityCompletion,
          requestToSelectProfessionalAfterActivityCompletion:
            values.requestToSelectProfessionalAfterActivityCompletion,
          compelToSelectProfessionalAfterActivityCompletion:
            values.requestToSelectProfessionalAfterActivityCompletion &&
            values.compelToSelectProfessionalAfterActivityCompletion,
        },
        professionalSettings: {},
      });
    },
  });

  return (
    <ModalBase.Wrapper
      size="lg"
      isOpen={isOpen}
      toggle={toggleModal}
      loading={false}
      onClosed={() => validation.resetForm()}
    >
      <Form
        autoComplete="off"
        onSubmit={(e) => {
          e.preventDefault();
          validation.handleSubmit();
          return false;
        }}
      >
        <ModalBase.Header toggle={toggleModal} withBorderBottom>
          {courseSettings?.name
            ? `Configurações - ${courseSettings.name}`
            : 'Configurações do Curso'}
        </ModalBase.Header>

        <ModalBase.Body>
          <Row>
            <Col md={12}>
              <OverlineTitle title="Configurações acadêmicas" />

              <div className="mb-4">
                <Label
                  htmlFor="locationToleratedDistanceInMeters"
                  className="form-label"
                >
                  Tolerância de distância do local (metros)
                </Label>
                <Input
                  min={0}
                  type="number"
                  placeholder="Metros"
                  className="form-control"
                  id="locationToleratedDistanceInMeters"
                  name="locationToleratedDistanceInMeters"
                  onBlur={validation.handleBlur}
                  onChange={validation.handleChange}
                  value={validation.values.locationToleratedDistanceInMeters}
                />
              </div>
            </Col>
          </Row>

          <Row>
            <Col md={6}>
              <OverlineTitle title="Configurações do estudante" />

              <div className="form-check mb-2">
                <Input
                  type="checkbox"
                  className="form-check-input"
                  id="isProfessionalMandatoryInActivity"
                  name="isProfessionalMandatoryInActivity"
                  onBlur={validation.handleBlur}
                  onChange={validation.handleChange}
                  checked={
                    !!validation.values.isProfessionalMandatoryInActivity
                  }
                />
                <label
                  className="form-check-label"
                  htmlFor="isProfessionalMandatoryInActivity"
                >
                  Exigir profissional na atividade
                </label>
              </div>

              <div className="form-check">
                <Input
                  type="checkbox"
                  className="form-check-input"
                  onBlur={validation.handleBlur}
                  onChange={validation.handleChange}
                  id="allowEvaluationAcceptanceByStudent"
                  name="allowEvaluationAcceptanceByStudent"
                  checked={
                    !!validation.values.allowEvaluationAcceptanceByStudent
                  }
                />
                <Label
                  htmlFor="allowEvaluationAcceptanceByStudent"
                  className="form-label"
                >
                  Disponibilizar aceite da avaliação pelo estudante
                </Label>
              </div>

              <div className="form-check mb-2">
                <Input
                  type="checkbox"
                  className="form-check-input"
                  id="allowCidCiapUponActivityCompletion"
                  name="allowCidCiapUponActivityCompletion"
                  onBlur={validation.handleBlur}
                  onChange={validation.handleChange}
                  checked={
                    !!validation.values.allowCidCiapUponActivityCompletion
                  }
                />
                <label
                  className="form-check-label"
                  htmlFor="allowCidCiapUponActivityCompletion"
                >
                  Disponibilizar CID/CIAP na finalização da atividade
                </label>
              </div>

              <div className="form-check mb-2">
                <Input
                  type="checkbox"
                  className="form-check-input"
                  id="requestToSelectProfessionalAfterActivityCompletion"
                  name="requestToSelectProfessionalAfterActivityCompletion"
                  onBlur={validation.handleBlur}
                  onChange={(e) => {
                    validation.handleChange(e);
                    if (!e.target.checked) {
                      validation.setFieldValue(
                        'compelToSelectProfessionalAfterActivityCompletion',
                        false,
                      );
                    }
                  }}
                  checked={
                    !!validation.values
                      .requestToSelectProfessionalAfterActivityCompletion
                  }
                />
                <label
                  className="form-check-label"
                  htmlFor="requestToSelectProfessionalAfterActivityCompletion"
                >
                  Solicitar que o estudante selecione o profissional após a
                  finalização da atividade (opcional)
                </label>
              </div>

              <div className="form-check mb-2">
                <Input
                  type="checkbox"
                  className="form-check-input"
                  id="compelToSelectProfessionalAfterActivityCompletion"
                  name="compelToSelectProfessionalAfterActivityCompletion"
                  onBlur={validation.handleBlur}
                  onChange={validation.handleChange}
                  disabled={
                    !validation.values
                      .requestToSelectProfessionalAfterActivityCompletion
                  }
                  checked={
                    !!validation.values
                      .compelToSelectProfessionalAfterActivityCompletion
                  }
                />
                <label
                  className="form-check-label"
                  htmlFor="compelToSelectProfessionalAfterActivityCompletion"
                >
                  Tornar obrigatório que o estudante selecione o profissional
                  após a finalização da atividade
                </label>
              </div>
            </Col>

            <Col md={6}>
              <OverlineTitle title="Configurações do profissional" />

              <p className="text-muted mb-3">
                Nenhuma configuração disponível no momento.
              </p>
            </Col>
          </Row>
        </ModalBase.Body>

        <ModalBase.Footer>
          <ModalBase.Actions
            actionBtnType="submit"
            goBack={toggleModal}
            loading={requestUpdateCourseFromEntityStatus === STATUSPAGE.LOADING}
          />
        </ModalBase.Footer>
      </Form>
    </ModalBase.Wrapper>
  );
};
